import type { AnalyzedMoveResult } from "@/shared/api/engine.api";
import { titleCase } from "./review-format";

export const motifLabel = (motif: string) => {
  const opponent = motif.startsWith("opponent_") ? "Opponent " : "";
  const base = motif.replace(/^opponent_/, "");
  return base === "imprecision" ? `${opponent}Inaccuracy` : `${opponent}${titleCase(base)}`;
};

const pickCopy = (options: string[], seed: string) =>
  options[[...seed].reduce((total, character) => total + character.charCodeAt(0), 0) % options.length];

export const motifDescription = (motif: string, move?: Pick<AnalyzedMoveResult, "moveNumber" | "san">) => {
  const opponent = motif.startsWith("opponent_");
  const subject = opponent ? "Your opponent's move" : "Your move";
  const base = motif.replace(/^opponent_/, "");

  if (base === "checkmate") {
    const seed = `${move?.moveNumber ?? 0}-${move?.san ?? motif}`;
    return pickCopy(
      opponent
        ? [
            "Nikola's call: checkmate. Your king is trapped.",
            "Checkmate. Nikola's note: your king had nowhere to go.",
            "Nikola's verdict: game over. The king was trapped.",
            "That's checkmate. Nikola's reminder: give your king an escape square.",
            "Checkmate. Nikola quietly closes the scorebook.",
            "Nikola sees it clearly: checkmate. Your king cannot get away.",
            "The king is stuck. Nikola calls checkmate.",
            "Checkmate. Nikola's takeaway: keep a square open for your king.",
            "Nikola's final word: trapped king, finished game.",
            "No escape for the king. Nikola marks this one checkmate.",
            "Checkmate. Nikola says this is where the game ends.",
            "The king has nowhere safe to stand. Nikola calls it.",
          ]
        : [
            "Nikola's call: checkmate. Their king is trapped. Clean finish.",
            "Checkmate. Nikola likes that one: the king has nowhere to go.",
            "Nikola's verdict: game over. You trapped the king.",
            "That's checkmate. Nikola stamps it: clean finish.",
            "Checkmate. Nikola quietly closes the scorebook.",
            "Nikola sees it clearly: checkmate. Their king cannot get away.",
            "The king is stuck. Nikola calls checkmate. Nicely done.",
            "Checkmate. Nikola's note: you gave their king no safe square.",
            "Nikola's final word: trapped king, finished game.",
            "No escape for their king. Nikola marks this one checkmate.",
            "Checkmate. Nikola says this is how you finish a game.",
            "Their king has nowhere safe to stand. Nikola calls it.",
          ],
      seed,
    );
  }
  const descriptions: Record<string, string> = {
    book: "A standard opening move that follows established theory.",
    brilliant: `${subject} found an exceptional engine-approved idea.`,
    best: `${subject} matched the engine's top choice.`,
    excellent: `${subject} was very strong with minimal evaluation loss.`,
    good: `${subject} kept the position in good shape.`,
    blunder: `${subject} significantly worsened the position.`,
    mistake: `${subject} gave away a meaningful advantage.`,
    imprecision: `${subject} was playable, but a stronger option was available.`,
    time_pressure: "This move was made with very little time remaining.",
    low_time: "This move used a large share of the remaining clock.",
  };
  return descriptions[base] ?? "A classification from Nikola's analysis.";
};

export const tacticalDescription = (motif: string) =>
  (
    ({
      engine_opportunity: "The verified better line begins with a forcing tactical move.",
      back_rank_threat: "A threat to mate or win material along the back rank because the king has too little shelter.",
      deflection: "A move lures a defender away from a square, piece, or duty it was protecting.",
      discovered_attack: "Moving one piece uncovers an attack from another piece behind it.",
      fork: "One piece attacks two or more targets at once, making it hard to save them all.",
      hanging_piece: "A piece can be taken without adequate compensation or protection.",
      mating_net: "The king is boxed in by coordinated threats, creating a forced mating sequence.",
      missed_capture: "A tactically important capture was available but not played.",
      missed_check: "A forcing check was available but not played.",
      overloaded_defender: "One defender is responsible for too many threats and cannot cover them all.",
      pin: "A piece cannot move freely because doing so would expose a more valuable piece or the king.",
      sacrifice: "Material is deliberately given up to gain a tactical or positional return.",
      skewer: "A valuable piece is attacked first, with a less valuable target behind it.",
      zwischenzug: "An in-between forcing move changes the expected order of captures or exchanges.",
    }) satisfies Record<string, string>
  )[motif] ?? "Nikola verified a tactical opportunity in this position.";
